import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-intl';
import { useDispatch } from 'react-redux';
import { isClient, SIGNING_AUDIT_TRAIL_TYPE } from '../../helpers/utils';
import { isAddonInstance } from '../../helpers/instanceHandler';
import styles from './order-summary.module.scss';
import { setLoader } from '../../store/actions';
import getSignedAgreement from '../../services/api/actions/getSignedAgreement';

const OrderSummary = ({ isOrderPlaced, isPreviousOrder }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const usernameTxt = intl.formatMessage({ id: 'order-summary.username' });
  const orderTxt = intl.formatMessage({ id: 'order-summary.order' });
  const dateTxt = intl.formatMessage({ id: 'order-summary.date' });
  const paymentTxt = intl.formatMessage({ id: 'order-summary.payment' });
  const addressTxt = intl.formatMessage({ id: 'order-summary.address' });
  const subtotalTxt = intl.formatMessage({ id: 'order-summary.subtotal_payment' });
  const downloadAgreementTxt = intl.formatMessage({
    id: 'order-summary.download_agreement_details',
  });
  const [auditTrialId, setAuditTrialId] = useState('');
  const info = isClient
    ? JSON.parse(localStorage.getItem(isPreviousOrder ? 'POR-boughtProducts' : 'boughtProducts')) ||
      {}
    : {};

  const {
    userName,
    order,
    date,
    paymentMethod,
    address,
    products,
    subtotal,
    fromEWalletAmount,
    amountFee,
    amountVat,
  } = info || {};
  useEffect(() => {
    if (isClient && SIGNING_AUDIT_TRAIL_TYPE.SIGNING_COMPLETED) {
      const id = localStorage.getItem('audit-trial-id');
      if (id) {
        setAuditTrialId(id);
      }
    }
  }, []);

  const details = [
    {
      label: usernameTxt,
      value: userName,
    },
    {
      label: orderTxt,
      value: order,
    },
    {
      label: dateTxt,
      value: date,
    },
    {
      label: paymentTxt,
      value: paymentMethod,
    },
    {
      label: addressTxt,
      value: address,
    },
  ];

  const isEWallet = ['e-wallet', 'ewallet'].includes(paymentMethod);

  const finalUserPrice =
    subtotal || (fromEWalletAmount || 0) + (Number(amountFee) || 0) + (Number(amountVat) || 0);

  const downloadAgreementFiles = async (e) => {
    e.preventDefault();
    if (!isClient) {
      return;
    }
    dispatch(setLoader(true));
    const res = await dispatch(getSignedAgreement.withQuery(`${auditTrialId}`).action());
    dispatch(setLoader(false));
    if (res.payload.data?.data?.url) {
      if (isClient) {
        const url = await fetch(res.payload.data?.data?.url)
          .then((r) => r.blob())
          .then((blob) => window.URL.createObjectURL(blob));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'agreement.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    }
  };

  const createProductLink = (path) => {
    if (isAddonInstance) {
      return '/';
    }
    return `/pricing/${path}`;
  };

  return (
    <div className={styles.order}>
      <div>
        {details.map(({ label, value }) => (
          <DetailsRow key={label} label={label} value={value} />
        ))}
      </div>
      <div className={styles.divider} />
      <div>
        {products &&
          products.length > 0 &&
          products.map(({ name, price, shopFullPrice, path }) => (
            <ProductRow link={createProductLink(path)} name={name} price={shopFullPrice || price} />
          ))}

        {(isEWallet || fromEWalletAmount) && (
          <div className={styles.subtotalWrapper}>
            <div>Subtotal E-Wallet</div>
            <div className={styles.subtotal}>{`${fromEWalletAmount || 0}$`}</div>
          </div>
        )}
        {isOrderPlaced && (
          <>
            {!!amountVat && (
              <div className={styles.subtotalWrapper}>
                <div>Vat</div>
                <div className={styles.subtotal}>{`${amountVat || 0}$`}</div>
              </div>
            )}

            <div className={styles.subtotalWrapper}>
              <div>Payment processor fee</div>
              <div className={styles.subtotal}>{`${amountFee || 0}$`}</div>
            </div>

            <div className={styles.subtotalWrapper}>
              <div>{subtotalTxt}</div>
              <div className={styles.subtotal}>{`${finalUserPrice}$`}</div>
            </div>
            <div className={styles.divider} />
          </>
        )}
      </div>
      {/* <div className={styles.action}> */}
      {/*  {isOrderPlaced && ( */}
      {/*    <span> */}
      {/*      <i className="icon-download" /> */}
      {/*      <a href="/" onClick={onDownloadClick}> */}
      {/*        {downloadInvoiceTxt} */}
      {/*      </a> */}
      {/*    </span> */}
      {/*  )} */}
      {/* </div> */}
      {auditTrialId && (
        <div>
          <div className={styles.action}>
            <span>
              <i className="icon-download" />
              <a href="/" onClick={(e) => downloadAgreementFiles(e)}>
                {downloadAgreementTxt}
              </a>
            </span>
          </div>
        </div>
      )}
      {/* //TODO: E_SIGNATURE */}
      {/* <div onClick={openInNewTab} className={styles.signedPdf}> */}
      {/*  View signed document */}
      {/* </div> */}
    </div>
  );
};
OrderSummary.defaultProps = {
  isPreviousOrder: false,
};
OrderSummary.propTypes = {
  isOrderPlaced: PropTypes.bool.isRequired,
  isPreviousOrder: PropTypes.bool,
};

export default OrderSummary;

function DetailsRow({ label, value }) {
  return (
    <div className={styles.detailsRow}>
      <div className={styles.detailsLeftCol}>{label}</div>
      <div className={styles.detailsRightCol}>{value}</div>
    </div>
  );
}

DetailsRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

function ProductRow({ name, price }) {
  return (
    <div className={styles.productRow}>
      <div className={styles.productLeftCol}>{name}</div>
      <div className={styles.productRightCol}>
        <div className={styles.productPrice}>{`${price}$`}</div>
      </div>
    </div>
  );
}

ProductRow.propTypes = {
  name: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
};
